/**
 * Children API service module
 * Provides typed functions for child profile management endpoints.
 *
 * Implements US-004 (T1): createChild and renameChild service functions.
 * Modified US-002 (T-002): added resetChildPassword service function.
 * Reuses the shared axios instance from lib/api.ts (parent.ts propagate-errors pattern).
 *
 * TO-LL-044 enforcement: request body keys are exactly displayName/password/username
 * (camelCase), matching the Python handler (children.py lines 126-128, 165).
 * TO-LL-036 enforcement: errors are never caught/swallowed here; they propagate to the
 * React Query mutation layer so AddChildDialog / RenameChildDialog / ResetChildPasswordDialog
 * can surface them inline.
 *
 * All functions reuse the shared axios client which auto-attaches
 * Authorization: Bearer {idToken} via the request interceptor and transforms
 * HTTP error codes (409 -> CONFLICT, 400 -> VALIDATION_ERROR, 403 -> FORBIDDEN)
 * via the response interceptor — consistent with parent.ts pattern.
 */

import { api } from '@/lib/api';
import type { CreateChildPayload, CreatedChild, RenamedChild, ResetChildPasswordResult } from '@/types/children';
import type { DifficultyTier } from '@/types/coins';

/**
 * Create a new child profile linked to the authenticated parent.
 *
 * POST /children
 * Body: { displayName, password, username? }
 * Response 201: { userId, username, displayName, parentUserId }
 *
 * Errors propagate (not caught) — React Query mutation surfaces them to AddChildDialog.
 * TO-LL-044: body keys are exact camelCase matching children.py lines 126-128.
 * TO-LL-036: no try/catch; 409/400/403/500 surface as AuthError {code, message}.
 *
 * @param payload - { displayName, password, username? }
 * @returns CreatedChild (userId, username, displayName, parentUserId)
 */
export async function createChild(payload: CreateChildPayload): Promise<CreatedChild> {
  const response = await api.post<CreatedChild>('/children', payload);
  return response.data;
}

/**
 * Rename a child's display name. The child's userId and Cognito login are never modified.
 *
 * PATCH /children/{childId}
 * Body: { displayName }  — NOTE: route is /children/{childId}, NOT /children/{childId}/rename
 * Response 200: { userId, displayName }
 *
 * Errors propagate (not caught) — React Query mutation surfaces them to RenameChildDialog.
 * TO-LL-044: body key is exactly displayName (camelCase), matching children.py line 165.
 * TO-LL-036: no try/catch; 400/403/404/500 surface as AuthError {code, message}.
 *
 * @param childId    - The child's userId (Cognito sub)
 * @param displayName - New display name to set (trimmed by caller before passing)
 * @returns RenamedChild (userId, displayName)
 */
export async function renameChild(childId: string, displayName: string): Promise<RenamedChild> {
  const response = await api.patch<RenamedChild>(`/children/${childId}`, { displayName });
  return response.data;
}

/**
 * Set a child's English difficulty tier (internal value, never a parent-facing label).
 *
 * Implements TS-007 (T2, SR6, API-003). Cloned from renameChild above: same
 * PATCH /children/{childId} route, same shared axios instance and error-propagation
 * contract; only the body key and response shape differ.
 *
 * PATCH /children/{childId}
 * Body: { difficultyTier }
 * Response 200: { userId, displayName, difficultyTier }
 *
 * Errors propagate (not caught) — useUpdateChildDifficulty (T4) surfaces them and
 * rolls back the optimistic cache write.
 * TO-LL-044: body key is exactly difficultyTier (camelCase), matching API-003.
 * TO-LL-036: no try/catch; 400/403/404/500 surface as AuthError {code, message}.
 *
 * @param childId       - The child's userId (Cognito sub)
 * @param difficultyTier - New learning level (DifficultyTier: the three P6 bands or P4/P5)
 * @returns { userId, displayName, difficultyTier }
 */
export async function setChildDifficulty(
  childId: string,
  difficultyTier: DifficultyTier
): Promise<{ userId: string; displayName: string; difficultyTier: string }> {
  const response = await api.patch<{ userId: string; displayName: string; difficultyTier: string }>(
    `/children/${childId}`,
    { difficultyTier }
  );
  return response.data;
}

/**
 * Reset a child's password. Only the password changes; userId/username/email/group are untouched.
 *
 * Implements US-002 (T-002). Mirrors renameChild (api.patch -> api.post, different path).
 *
 * POST /children/{childId}/reset-password
 * Body: { password }
 * Response 200: { userId }
 *
 * Errors propagate (not caught) — React Query mutation surfaces them to ResetChildPasswordDialog.
 * TO-LL-044: body key is exactly `password` (camelCase matches backend reset handler).
 * TO-LL-036: no try/catch; 400/401/403/500 surface as AuthError {code, message}
 *            via the shared axios response interceptor (api.ts:50-57).
 * Security: the password is sent only in the request body and is never logged here.
 *
 * @param childId  - The child's userId (Cognito sub)
 * @param password - New password meeting Cognito policy (validated client-side before this call)
 * @returns ResetChildPasswordResult ({ userId })
 */
export async function resetChildPassword(
  childId: string,
  password: string,
): Promise<ResetChildPasswordResult> {
  const response = await api.post<ResetChildPasswordResult>(
    `/children/${childId}/reset-password`,
    { password },
  );
  return response.data;
}
